/**
 * About InsideAccess — content from ABOUT_PAGE.md
 */
import { useTheme } from '@/contexts/ThemeContext'
import logoSvg from '@/assets/sensorhub-logo.svg?raw'

const themeToFill: Record<string, string> = {
  light: '#000000',
  dark: '#FFFFFF',
  'yellow-black': '#FFFF00',
}

export default function AboutPage() {
  const { theme } = useTheme()
  const logoFill = themeToFill[theme] ?? '#000000'
  const sectionClass = 'mb-8'
  const h2Class = 'text-xl font-bold mb-4'
  const pClass = 'mb-4'
  const ulClass = 'list-disc pl-6 space-y-2 mb-4'
  const textPrimary = { color: 'var(--text-primary)' as const }
  const textSecondary = { color: 'var(--text-secondary)' as const }
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <article aria-labelledby="about-title">
        <h1 id="about-title" className="text-3xl font-bold mb-8" style={textPrimary}>
          About InsideAccess
        </h1>

        <section className={sectionClass} aria-labelledby="what-is">
          <h2 id="what-is" className={h2Class} style={textPrimary}>
            What is InsideAccess?
          </h2>
          <p className={pClass} style={textSecondary}>
            InsideAccess is a community-driven platform designed to make it easier to{' '}
            <strong>discover, discuss, and share information about accessible media content</strong>.
          </p>
          <p className={pClass} style={textSecondary}>
            Our focus is on content that includes accessibility features such as:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li>Audio Description (AD)</li>
            <li>Subtitles and captions</li>
            <li>Signed content</li>
            <li>Other accessibility aids that support different access needs</li>
          </ul>
          <p style={textSecondary}>
            InsideAccess brings together{' '}
            <strong>end users, broadcasters, content creators, and accessibility advocates</strong>{' '}
            in one place, helping improve awareness, quality, and availability of accessible content
            across TV and streaming platforms.
          </p>
        </section>

        <section className={sectionClass} aria-labelledby="key-features">
          <h2 id="key-features" className={h2Class} style={textPrimary}>
            Key Features
          </h2>
          <p className={pClass} style={textSecondary}>
            InsideAccess is built to be simple, inclusive, and useful. Key features include:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li><strong>Community discussions</strong> about accessible programmes and services</li>
            <li><strong>Program listings and references</strong> highlighting known accessibility features</li>
            <li><strong>Links to useful resources</strong>, news, and guidance</li>
            <li><strong>Feedback channels</strong> so users can share experiences about availability and quality</li>
            <li><strong>Direct engagement opportunities</strong> with broadcasters and content creators</li>
          </ul>
          <p style={textSecondary}>
            Our aim is to provide a single, trusted place where accessibility-related information
            and conversations can live and grow.
          </p>
        </section>

        <section className={sectionClass} aria-labelledby="when-launched">
          <h2 id="when-launched" className={h2Class} style={textPrimary}>
            When We Launched
          </h2>
          <p className={pClass} style={textSecondary}>
            InsideAccess is launching in <strong>February 2026</strong>, with an initial public
            release targeted for <strong>9 February 2026</strong>.
          </p>
          <p style={textSecondary}>
            The platform will continue to evolve after launch, shaped by community feedback and
            real-world use.
          </p>
        </section>

        <section className={sectionClass} aria-labelledby="who-behind">
          <h2 id="who-behind" className={h2Class} style={textPrimary}>
            Who Is Behind InsideAccess?
          </h2>
          <p className={pClass} style={textSecondary}>
            InsideAccess is developed and operated by <strong>SensorMedia Hub</strong>, a team
            focused on improving access, inclusion, and understanding across digital and broadcast
            media.
          </p>
          <p className={pClass} style={textSecondary}>
            SensorMedia Hub brings together experience in:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li>Accessibility-focused product development</li>
            <li>Media and technology platforms</li>
            <li>Community-led digital services</li>
          </ul>
        </section>

        <section className={sectionClass} aria-labelledby="broadcasters">
          <h2 id="broadcasters" className={h2Class} style={textPrimary}>
            Broadcasters and Content Providers
          </h2>
          <p className={pClass} style={textSecondary}>
            At launch, InsideAccess includes and references content from:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li><strong>BBC</strong></li>
          </ul>
          <p className={pClass} style={textSecondary}>
            Looking ahead, we aim to work with and include information from:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li>Most major UK terrestrial broadcasters</li>
            <li>Other UK and international TV channels</li>
            <li>Major streaming services</li>
          </ul>
          <p style={textSecondary}>
            Our long-term goal is to support <em>whatever content people want to watch</em>, by
            providing the best possible information about the accessibility tools available.
          </p>
        </section>

        <section className={sectionClass} aria-labelledby="who-for">
          <h2 id="who-for" className={h2Class} style={textPrimary}>
            Who InsideAccess Is For
          </h2>
          <p className={pClass} style={textSecondary}>
            InsideAccess is designed primarily for <strong>end users</strong>, including:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li>Blind and partially sighted users</li>
            <li>Deaf and hard-of-hearing users</li>
            <li>Neurodivergent users</li>
            <li>Anyone who benefits from accessible media features</li>
          </ul>
          <p className={pClass} style={textSecondary}>
            We also welcome:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li>Broadcasters and content providers</li>
            <li>Accessibility professionals</li>
            <li>Advocates and researchers</li>
          </ul>
          <p style={textSecondary}>
            Everyone is encouraged to participate respectfully and constructively.
          </p>
        </section>

        <section className={sectionClass} aria-labelledby="rules">
          <h2 id="rules" className={h2Class} style={textPrimary}>
            Rules of the Road
          </h2>
          <p className={pClass} style={textSecondary}>
            InsideAccess is a shared community space. To keep it welcoming and useful for everyone,
            we ask all users to follow these simple principles:
          </p>
          <ul className={ulClass} style={textSecondary}>
            <li>Be respectful and considerate of others</li>
            <li>Share experiences honestly, but constructively</li>
            <li>Avoid personal attacks, abusive language, or discriminatory comments</li>
            <li>Remember that people&apos;s accessibility needs and experiences vary</li>
          </ul>
          <h3 className="text-lg font-semibold mb-2 mt-6" style={textPrimary}>
            Moderation and Reporting
          </h3>
          <ul className={ulClass} style={textSecondary}>
            <li>Content on InsideAccess is <strong>monitored and moderated</strong></li>
            <li>Users can <strong>report posts or comments</strong> they believe are inappropriate, harmful, or abusive</li>
            <li>We will review reports and take action where necessary, which may include removing content or restricting accounts</li>
          </ul>
          <p style={textSecondary}>
            Our goal is not to silence discussion, but to ensure conversations remain safe,
            inclusive, and productive.
          </p>
        </section>

        <section className={sectionClass} aria-labelledby="looking-ahead">
          <h2 id="looking-ahead" className={h2Class} style={textPrimary}>
            Looking Ahead
          </h2>
          <p className={pClass} style={textSecondary}>
            We&apos;re excited to bring InsideAccess to life and to grow it alongside the community
            that uses it.
          </p>
          <p className={pClass} style={textSecondary}>
            Your feedback, ideas, and experiences will play a vital role in shaping how the platform
            develops. We&apos;re keen to hear what works, what doesn&apos;t, and how we can make
            InsideAccess better for everyone.
          </p>
          <p className={pClass} style={textSecondary}>
            Thank you for being part of the InsideAccess journey.
          </p>
          <p className="mt-8 font-semibold" style={textPrimary}>
            The InsideAccess team
          </p>
        </section>

        <div
          className="mt-10 pt-8 border-t-2 flex flex-col items-center gap-4"
          style={{ borderColor: 'var(--bg-tertiary)' }}
          aria-hidden
        >
          <div
            className="pcp-brand-logo-fill"
            style={{
              ['--pcp-brand-logo-fill' as string]: logoFill,
              height: '4rem',
              width: 'auto',
              minWidth: '3rem',
            } as React.CSSProperties}
          >
            <div
              style={{ width: '100%', height: '100%' }}
              dangerouslySetInnerHTML={{ __html: logoSvg }}
            />
          </div>
        </div>
      </article>
    </div>
  )
}
